/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  AppCSS,
  Lbl,
  Spacer,
} from "../../../../components";
import { RowDiv, ColDiv } from "../../../../components/misc/misc.styled";
import { Customer, Progress, Project } from "../../../../redux/types";
import { FC, useEffect, useState } from "react"; // Remove useMemo
import { Prompt } from "../../../../components/dialogue/Dialogue";
import { EditProgressSteps } from "../../../../redux/api";
import { useFetchProductSytems } from "../../../../apolloClient/hooks/general/fetchHook";
import { ProductSystemDetails } from "../../../../redux/types/projectDetails";
import { useEditProductSystem } from "../../../../apolloClient/hooks/itemCreation/createHooks"; // Import useEditProductSystem
import { UpdateProductSystemInput } from "../../../../types/graphQlQuery.interface"; // Import UpdateProductSystemInput
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";

interface ProductSystemProps {
  handleRefetchProjectDetails: Function
  onFetchProgress: Function;
  progress?: Progress[];
  project?: Project;
  customerDetails?: Customer;
  productData?: ProductSystemDetails[]; // Add productData property
}

interface StepChangeAction {
  step: string;
  forward: boolean;
}

export const ProductSystem: FC<ProductSystemProps> = ({
  handleRefetchProjectDetails,
  onFetchProgress,
  project,
  customerDetails,
  productData, // Use productData prop
}) => {
  let { id } = useParams();
  const { error, loading, productSystems, refetch } = useFetchProductSytems(); // Fetch product systems
  const {
    editProductSystem,
    loading: editProductSystemLoading,
    error: editProductSystemError,
  } = useEditProductSystem(); // Use editProductSystem hook

  const [openConfirm, setOpenConfirm] = useState(false);
  const [stepToChange, setStepToChange] = useState<StepChangeAction>();
  const [stepChangeMsg, setStepChangeMsg] = useState(
    "This will revert or advance the project to the selected step."
  );

  const [selectedProducts, setSelectedProducts] = useState<number[]>([]); // State for selected products

  useEffect(() => {
    // Initialize selected products based on productData
    if (productData) {
      const initialSelectedProducts = productData.map((product) => product.id);
      setSelectedProducts(initialSelectedProducts);
    }
  }, [productData]);

  const toggleProductSelection = (productId: number) => {
    setSelectedProducts((prev) =>
      prev.includes(productId)
        ? prev.filter((id) => id !== productId)
        : [...prev, productId]
    );
  };

  const handleSaveProductSystemChanges = async () => {
    try {
      const input: UpdateProductSystemInput = {
        productIds: selectedProducts,
        projectId: Number(id),
      };

      const success = await editProductSystem(input);
      handleRefetchProjectDetails();

      if (!success) {
        toast.error("Failed to save changes. Please try again."); 
        return;
      }
      toast.success("Changes saved successfully!");
    } catch (error) {
      console.error("Error saving changes:", error);
      toast.error("An error occurred while saving changes. Please try again.");
    }
  };

  const changeCustomerStage = (stage: StepChangeAction) => {
    setStepChangeMsg(
      `This will ${
        stage.forward ? "advance" : "revert"
      } the project to the selected step.`
    );
    setOpenConfirm(true);
    setStepToChange(stage);
  };

  const customerChangeConfirmation = async () => {
    setOpenConfirm(false);
    try {
      const status = await EditProgressSteps(
        project.id,
        stepToChange.step,
        stepToChange.forward ? "forward" : "reverse"
      );
      if (status === 200) {
        onFetchProgress();
      }
      // TODO: show errors
    } catch (error) {}
  };

  return (
    <>
      <div
        style={{
          padding: "16px",
          backgroundColor: "#f8fafc",
          minHeight: "100%",
        }}
      >
        <Prompt
          message={stepChangeMsg}
          onConfirm={() => {
            customerChangeConfirmation();
          }}
          onClose={() => {
            setStepToChange({} as StepChangeAction);
            setOpenConfirm(false);
          }}
          open={openConfirm}
        />
        <div
          style={{
            maxWidth: "1200px",
            margin: "0 auto",
          }}
        >
          {/* right side */}
          <div
            style={{
              width: "100%",
              maxWidth: "800px",
              margin: "0 auto",
            }}
          >
            <h2 style={{ textAlign: "center", marginBottom: "16px", fontSize: "20px", fontWeight: 600, color: "#1f2937" }}>
              Select Product Systems
            </h2>
            <p
              style={{
                textAlign: "center",
                marginBottom: "20px",
                color: "#6b7280",
                fontSize: "14px",
                lineHeight: "1.5",
              }}
            >
              Choose the product systems you want to include in your project. Scroll
              to see more options.
            </p>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                flexWrap: "wrap",
                gap: "16px",
                maxHeight: "400px",
                overflowY: "auto",
                padding: "16px",
                border: "1px solid #e5e7eb",
                borderRadius: "8px",
                backgroundColor: "#ffffff",
              }}
            >
          {productSystems
            ?.filter((product) => !product.archived)
            .sort((a, b) => (a.rank ?? 0) - (b.rank ?? 0))
            .map((product) => (
              <div
                key={product.id}
                onClick={() => toggleProductSelection(product.id)}
                style={{
                  border: selectedProducts.includes(product.id)
                    ? "2px solid #4b87ed"
                    : "1px solid #e5e7eb",
                  borderRadius: "8px",
                  padding: "12px",
                  width: "180px",
                  textAlign: "center",
                  backgroundColor: selectedProducts.includes(product.id)
                    ? "#eff6ff"
                    : "#fff",
                  boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                }}
              >
                <img
                  src={product.thumbnail}
                  alt={product.name}
                  style={{
                    width: "100%",
                    height: "100px",
                    objectFit: "cover",
                    borderRadius: "6px",
                    marginBottom: "8px",
                  }}
                />
                <h4
                  style={{
                    margin: "8px 0",
                    fontSize: "14px",
                    fontWeight: 600,
                    color: "#1f2937",
                  }}
                >
                  {product.name}
                </h4>
                <p style={{ fontSize: "14px", color: "#777" }}>
                  {product.duration}
                </p>
              </div>
            ))}
          </div>
          <button
          onClick={handleSaveProductSystemChanges}
          style={{
            marginTop: "20px",
            padding: "10px 20px",
            backgroundColor: "#007BFF",
            color: "#fff",
            border: "none",
            borderRadius: "5px",
            cursor: "pointer",
            float: "right",
          }}
        >
          Save Changes
          </button>
        </div>
      </div>
    </div>
    </>
  );
};
